// ══════════════════════════════════════════════════════════
// DOMAINS SERVICE
//
// Dominios personalizados para radios Premium.
// Consume el backend PHP vía rfwBackend.
// Fallback: mock data cuando el backend no está disponible.
//
// FRONTEND NO PUEDE:
// - Modificar DNS directamente.
// - Activar SSL.
// - Marcar certificados como válidos.
// - Guardar API keys o credenciales de proveedores DNS.
// ══════════════════════════════════════════════════════════

import { backendApiRequest } from '@/services/rfwBackend';
import type {
  CustomDomainRequest,
  CustomDomainResponse,
  VerifyDomainResponse,
  DomainUIData,
  DomainStatus,
  DnsRecord,
  VerificationHistoryEntry,
} from '@/types/domains';
import { buildEmptyDomainUIData } from '@/types/domains';
import {
  radioDomainDataList,
  type RadioDomainData,
} from '@/mocks/radioDomains';

// ═══════════════ BACKEND API (PRIMARY) ═══════════════

/**
 * Solicitar un dominio personalizado para una radio.
 * POST /api/v1/radios/{radioId}/domains
 */
export async function requestDomain(
  radioId: string,
  domain: string,
): Promise<DomainUIData> {
  try {
    const response = await backendApiRequest<CustomDomainResponse>(
      `/api/v1/radios/${radioId}/domains`,
      {
        method: 'POST',
        body: { domain } as Record<string, unknown>,
      },
    );

    if (response.success && response.data) {
      return mapResponseToUIData(response.data);
    }

    throw new Error(response.message || 'Error al solicitar dominio');
  } catch (err) {
    // Re-lanzar errores del backend para que el UI muestre el mensaje
    if (err instanceof Error && err.message.includes('backend')) {
      throw err;
    }
    throw new Error(
      err instanceof Error ? err.message : 'Error desconocido al solicitar dominio',
    );
  }
}

/**
 * Obtener el estado del dominio de una radio.
 * GET /api/v1/radios/{radioId}/domains
 */
export async function getDomainStatus(radioId: string): Promise<DomainUIData> {
  try {
    const response = await backendApiRequest<CustomDomainResponse>(
      `/api/v1/radios/${radioId}/domains`,
    );

    if (response.success && response.data) {
      return mapResponseToUIData(response.data);
    }

    throw new Error('Backend sin datos de dominio');
  } catch {
    // Fallback a mock
    return getMockDomainData(radioId);
  }
}

/**
 * Solicitar verificación DNS del dominio.
 * POST /api/v1/radios/{radioId}/domains/{domainId}/verify
 */
export async function verifyDomain(
  radioId: string,
  domainId: string,
): Promise<VerifyDomainResponse> {
  try {
    const response = await backendApiRequest<VerifyDomainResponse>(
      `/api/v1/radios/${radioId}/domains/${domainId}/verify`,
      { method: 'POST' },
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Error al verificar dominio');
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Error desconocido al verificar DNS');
  }
}

/**
 * Establecer dominio como principal.
 * PUT /api/v1/radios/{radioId}/domains/{domainId}/primary
 */
export async function setPrimaryDomain(
  radioId: string,
  domainId: string,
): Promise<DomainUIData> {
  try {
    const response = await backendApiRequest<CustomDomainResponse>(
      `/api/v1/radios/${radioId}/domains/${domainId}/primary`,
      { method: 'PUT' },
    );

    if (response.success && response.data) {
      return mapResponseToUIData(response.data);
    }

    throw new Error(response.message || 'Error al activar dominio');
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Error desconocido al activar dominio');
  }
}

/**
 * Desactivar / eliminar un dominio.
 * DELETE /api/v1/radios/{radioId}/domains/{domainId}
 */
export async function removeDomain(
  radioId: string,
  domainId: string,
): Promise<DomainUIData> {
  try {
    const response = await backendApiRequest<CustomDomainResponse>(
      `/api/v1/radios/${radioId}/domains/${domainId}`,
      { method: 'DELETE' },
    );

    if (response.success && response.data) {
      return mapResponseToUIData(response.data);
    }

    throw new Error(response.message || 'Error al eliminar dominio');
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error('Error desconocido al eliminar dominio');
  }
}

// ═══════════════ MAPPING ═══════════════

function mapResponseToUIData(response: CustomDomainResponse): DomainUIData {
  return {
    domainId: response.id,
    domain: response.domain,
    status: response.status,
    sslStatus: response.ssl_status,
    sslExpiresAt: response.ssl_expires_at,
    dnsRecords: response.dns_records || [],
    verifiedAt: response.verified_at,
    activatedAt: response.activated_at,
    errorMessage: response.error_message,
    isPrimary: response.is_primary,
    verificationHistory: [],
  };
}

// ═══════════════ MOCK FALLBACK ═══════════════

function mapOldStatusToNew(oldStatus: string): DomainStatus {
  switch (oldStatus) {
    case 'no_configurado': return 'PENDING';
    case 'pendiente_dns': return 'DNS_REQUIRED';
    case 'verificado': return 'VERIFIED';
    case 'activo': return 'ACTIVE';
    case 'error': return 'FAILED';
    default: return 'PENDING';
  }
}

function mapMockDnsRecords(records: { type: string; host: string; value: string; ttl: number }[]): DnsRecord[] {
  return records.map((r) => ({
    type: r.type as DnsRecord['type'],
    host: r.host,
    value: r.value,
    ttl: r.ttl,
  }));
}

function mapMockVerificationHistory(
  entries: { id: number; timestamp: string; result: string; message: string }[],
): VerificationHistoryEntry[] {
  return entries.map((e) => ({
    id: String(e.id),
    timestamp: e.timestamp,
    result: e.result as 'success' | 'failure',
    message: e.message,
  }));
}

async function getMockDomainData(radioId: string): Promise<DomainUIData> {
  const numId = parseInt(radioId, 10);
  const mock = radioDomainDataList.find((r: RadioDomainData) => r.radioId === numId);

  if (!mock) {
    return buildEmptyDomainUIData();
  }

  return {
    domainId: mock.domain ? `mock-${radioId}` : null,
    domain: mock.domain || null,
    status: mapOldStatusToNew(mock.domainStatus),
    sslStatus: mock.domainStatus === 'activo' ? 'ACTIVE' : 'NOT_REQUESTED',
    sslExpiresAt: null,
    dnsRecords: mapMockDnsRecords(mock.dnsInstructions),
    verifiedAt: mock.verifiedAt,
    activatedAt: mock.activatedAt,
    errorMessage: mock.errorMessage,
    isPrimary: mock.domainStatus === 'activo',
    verificationHistory: mapMockVerificationHistory(mock.verificationHistory),
  };
}